import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Icon } from "../components/Icon";
import { Page, TopBar } from "../components/Layout";
import { LoadMore } from "../components/ListControls";
import { api } from "../lib/api";
import { onEvent } from "../lib/events";
import { markRead, NOTIFICATION_EVENT, refreshUnread, setUnread, type InboxItem } from "../lib/notify";
import { useToast } from "../lib/toast";
import { usePaged } from "../lib/usePaged";

const KIND_ICON: Record<string, string> = { people: "people", updates: "download", pipeline: "sparkle", shared: "share", location: "globe" };

export function ago(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 7 * 86_400) return `${Math.floor(s / 86_400)} d ago`;
  return new Date(iso).toLocaleDateString();
}

/** ADR-0031: everything Omni and your apps told you, newest first. */
export function Notifications() {
  const nav = useNavigate();
  const toast = useToast();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const list = usePaged<InboxItem>("/api/notifications", { sort: "-created_at", limit: 30, extra: unreadOnly ? { unread: "true" } : {} });
  const [read, setRead] = useState<Set<number>>(new Set());

  useEffect(() => onEvent((e) => e.type === NOTIFICATION_EVENT && !e.app_id && list.reload()), [list.reload]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => void refreshUnread().catch(() => undefined), [list.items]);

  const isRead = (n: InboxItem) => n.read || read.has(n.id);
  const open = (n: InboxItem) => {
    if (!isRead(n)) {
      setRead((s) => new Set(s).add(n.id));
      void markRead(n.id).catch(() => undefined);
    }
    if (n.link) nav(n.link);
  };
  const readAll = async () => {
    await api("/api/notifications/read-all", { method: "POST" });
    setUnread(0);
    setRead(new Set(list.items.map((n) => n.id)));
    if (unreadOnly) list.reload();
  };
  const remove = async (n: InboxItem) => {
    await api(`/api/notifications/${n.id}`, { method: "DELETE" });
    list.reload();
  };
  const clear = async () => {
    if (!confirm("Delete all your notifications?")) return;
    await api("/api/notifications", { method: "DELETE" });
    setUnread(0);
    list.reload();
    toast("Cleared", "success");
  };
  const anyUnread = list.items.some((n) => !isRead(n));

  return (
    <>
      <TopBar title="Notifications" avatar={false}
        back={<button className="icon-btn" aria-label="Back" onClick={() => (history.length > 1 ? nav(-1) : nav("/"))}><Icon name="chevron-left" /></button>}>
        <button className="icon-btn" aria-label="Mark all as read" title="Mark all as read" disabled={!anyUnread} onClick={readAll}><Icon name="check-all" /></button>
        <button className="icon-btn" aria-label="Notification settings" title="Settings" onClick={() => nav("/settings/notifications")}><Icon name="gear" /></button>
      </TopBar>
      <Page>
        <div className="segmented" role="radiogroup" aria-label="Show">
          <button role="radio" aria-checked={!unreadOnly} className={!unreadOnly ? "active" : ""} onClick={() => setUnreadOnly(false)}>All</button>
          <button role="radio" aria-checked={unreadOnly} className={unreadOnly ? "active" : ""} onClick={() => setUnreadOnly(true)}>Unread</button>
        </div>
        {list.error && <p className="error small">{list.error}</p>}
        {!list.loading && list.total === 0 && (
          <div className="card stack center" style={{ padding: "32px 16px" }}>
            <Icon name="bell" size={32} style={{ color: "var(--omni-text-muted)", margin: "0 auto" }} />
            <p className="muted">{unreadOnly ? "You're all caught up." : "No notifications yet. Friend requests, shared lists, pipelines and new versions of Omni show up here."}</p>
          </div>
        )}
        {list.items.length > 0 && (
          <div className="card flush">
            {list.items.map((n) => (
              <div key={n.id} className={`inbox-item${isRead(n) ? "" : " unread"}`}>
                <button className="open" onClick={() => open(n)}>
                  <span className="lead"><Icon name={KIND_ICON[n.kind] ?? "bell"} size={20} /></span>
                  <span className="txt">
                    <strong>{n.title}</strong>
                    {n.body && <span className="body">{n.body}</span>}
                    <span className="meta">{ago(n.created_at)}</span>
                  </span>
                </button>
                {!isRead(n) && <span className="dot" aria-label="Unread" />}
                <button className="icon-btn" style={{ width: 36, height: 36 }} aria-label={`Delete “${n.title}”`} onClick={() => remove(n)}><Icon name="close" size={16} /></button>
              </div>
            ))}
          </div>
        )}
        <LoadMore hasMore={list.hasMore} loading={list.loading} onClick={list.loadMore} shown={list.items.length} total={list.total} />
        {list.total > 0 && <button className="btn btn-ghost danger" onClick={clear}><Icon name="trash" size={18} />Clear all</button>}
      </Page>
    </>
  );
}
